import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/client";
import { formatDateTime } from "../utils/datetime";
import { useSort, sortRows, SortableTh } from "./SortableTh";

/* Local Windows user accounts for a device — list + enable/disable, reset
 * password (server-generated, shown once), add/remove local admin, delete.
 * Sibling of the Services / Scheduled Tasks tabs. */

type LUser = {
  name: string;
  full_name?: string;
  enabled: boolean;
  description?: string;
  last_logon?: string;
  is_admin?: boolean;
};

type Action = "enable" | "disable" | "reset_password" | "add_admin" | "remove_admin" | "delete";

export default function LocalUsersTab({
  deviceId,
  online,
  canManage,
}: {
  deviceId: number;
  online: boolean;
  canManage: boolean;
}) {
  const [users, setUsers] = useState<LUser[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [pwReveal, setPwReveal] = useState<{ user: string; password: string } | null>(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);
  const sort = useSort("name", "asc");

  const poll = useCallback(
    (cid: number, onDone: (r: any) => void) => {
      const started = Date.now();
      const tick = async () => {
        let status, result, em;
        try {
          const r = await api.get(`/api/v1/devices/${deviceId}/command/${cid}`);
          status = r.data?.status;
          result = r.data?.result;
          em = r.data?.error_message;
        } catch {
          /* transient */
        }
        if (status === "completed") return onDone(result);
        if (status === "failed") {
          setError(em || "The device reported a failure.");
          setLoading(false);
          setBusy(null);
          return;
        }
        if (Date.now() - started > 90000) {
          setError(online ? "Timed out waiting for the device." : "Device is offline.");
          setLoading(false);
          setBusy(null);
          return;
        }
        pollRef.current = window.setTimeout(tick, 2500);
      };
      pollRef.current = window.setTimeout(tick, 2000);
    },
    [deviceId, online],
  );

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    setNotice("");
    api
      .post(`/api/v1/devices/${deviceId}/local-users/list`)
      .then(({ data }) =>
        poll(data.command_id, (result) => {
          setUsers((result?.users || []).slice());
          setLoading(false);
        }),
      )
      .catch((e) => {
        setError(e.response?.data?.detail || "Failed to queue the command.");
        setLoading(false);
      });
  }, [deviceId, poll]);

  useEffect(() => {
    load();
    return () => {
      if (pollRef.current) window.clearTimeout(pollRef.current);
    };
  }, [load]);

  const act = (u: LUser, action: Action) => {
    const confirms: Partial<Record<Action, string>> = {
      delete: `Delete local user "${u.name}"? This cannot be undone.`,
      reset_password: `Reset the password for "${u.name}"? A new strong password will be generated and shown once.`,
      add_admin: `Add "${u.name}" to the local Administrators group?`,
      remove_admin: `Remove "${u.name}" from local Administrators?`,
    };
    if (confirms[action] && !window.confirm(confirms[action]!)) return;
    setBusy(u.name);
    setError("");
    setNotice("");
    api
      .post(`/api/v1/devices/${deviceId}/local-users/control`, { username: u.name, action })
      .then(({ data }) =>
        poll(data.command_id, () => {
          if (action === "reset_password" && data.generated_password) {
            setPwReveal({ user: u.name, password: data.generated_password });
          } else {
            setNotice(`${action.replace("_", " ")} succeeded for "${u.name}".`);
          }
          setBusy(null);
          load();
        }),
      )
      .catch((e) => {
        setError(e.response?.data?.detail || "Action failed.");
        setBusy(null);
      });
  };

  const shown = (users || []).filter((u) => {
    const q = search.trim().toLowerCase();
    return !q || u.name.toLowerCase().includes(q) || (u.full_name || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={load}
          disabled={loading}
          className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? "Loading…" : "↻ Refresh"}
        </button>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by name…"
          className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm ml-auto w-64"
        />
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      {notice && <p className="text-sm text-green-600 dark:text-green-400">{notice}</p>}

      {pwReveal && (
        <div className="rounded-lg border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3">
          <div className="text-sm text-emerald-800 dark:text-emerald-200">
            New password for <strong>{pwReveal.user}</strong> (shown once — copy it now):
          </div>
          <div className="flex items-center gap-3 mt-1">
            <code className="text-sm font-mono bg-white dark:bg-gray-800 border dark:border-gray-700 rounded px-2 py-1">
              {pwReveal.password}
            </code>
            <button
              onClick={() => navigator.clipboard?.writeText(pwReveal.password)}
              className="text-xs text-emerald-700 dark:text-emerald-300 hover:underline"
            >
              Copy
            </button>
            <button
              onClick={() => setPwReveal(null)}
              className="text-xs text-gray-500 hover:underline ml-auto"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {users && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
          <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
            {shown.length} local user{shown.length === 1 ? "" : "s"}
          </div>
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <SortableTh label="Name" sortKey="name" sort={sort.sort} onSort={sort.toggle} />
                <SortableTh label="Full name" sortKey="full_name" sort={sort.sort} onSort={sort.toggle} />
                <SortableTh label="Status" sortKey="enabled" sort={sort.sort} onSort={sort.toggle} />
                <SortableTh label="Admin" sortKey="is_admin" sort={sort.sort} onSort={sort.toggle} />
                <SortableTh label="Last logon" sortKey="last_logon" sort={sort.sort} onSort={sort.toggle} />
                {canManage && <th className="px-3 py-2"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-gray-700 dark:text-gray-300">
              {sortRows(shown, sort.sort, (u, key) => {
                switch (key) {
                  case "full_name": return (u.full_name || "").toLowerCase();
                  case "enabled": return u.enabled ? 1 : 0;
                  case "is_admin": return u.is_admin ? 1 : 0;
                  case "last_logon": return u.last_logon || "";
                  default: return (u.name || "").toLowerCase();
                }
              }).map((u) => {
                const isBusy = busy === u.name;
                return (
                  <tr key={u.name} className="group">
                    <td className="px-3 py-1.5 font-medium">{u.name}</td>
                    <td className="px-3 py-1.5 truncate max-w-[12rem]" title={u.description}>{u.full_name || "—"}</td>
                    <td className="px-3 py-1.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${u.enabled ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300"}`}>
                        {u.enabled ? "Enabled" : "Disabled"}
                      </span>
                    </td>
                    <td className="px-3 py-1.5">
                      {u.is_admin ? <span className="text-amber-600 dark:text-amber-400 font-semibold">Admin</span> : "—"}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{u.last_logon ? formatDateTime(u.last_logon) : "—"}</td>
                    {canManage && (
                      <td className="px-3 py-1.5 text-right whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                        {isBusy ? (
                          <span className="text-gray-400">…</span>
                        ) : (
                          <span className="inline-flex gap-2">
                            <button onClick={() => act(u, "reset_password")} className="text-blue-600 dark:text-blue-400 hover:underline">Reset pw</button>
                            {u.enabled ? (
                              <button onClick={() => act(u, "disable")} className="text-amber-600 dark:text-amber-400 hover:underline">Disable</button>
                            ) : (
                              <button onClick={() => act(u, "enable")} className="text-green-600 dark:text-green-400 hover:underline">Enable</button>
                            )}
                            {u.is_admin ? (
                              <button onClick={() => act(u, "remove_admin")} className="text-gray-600 dark:text-gray-300 hover:underline">Un-admin</button>
                            ) : (
                              <button onClick={() => act(u, "add_admin")} className="text-gray-600 dark:text-gray-300 hover:underline">Make admin</button>
                            )}
                            <button onClick={() => act(u, "delete")} className="text-rose-600 dark:text-rose-400 hover:underline">Delete</button>
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
